import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ordersAPI, workOrdersAPI } from '../../services/api';
import { generateNativePdf, printStyles, CompanyLetterhead } from './shared';

export function OrderProductionReportPrintView() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orderRes, woRes] = await Promise.all([
          ordersAPI.get(id),
          workOrdersAPI.getByOrder(id)
        ]);
        setOrder(orderRes.data);
        setWorkOrders(woRes.data?.work_orders || []);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!loading && order) {
      setTimeout(() => window.print(), 500);
    }
  }, [loading, order]);

  if (loading) return <div className="p-8 text-center">Loading production report...</div>;
  if (!order) return <div className="p-8 text-center">Order not found</div>;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const STATUS_INFO = {
    draft: { label: 'Planning', color: '#6b7280', bg: '#f3f4f6' },
    released: { label: 'Scheduled', color: '#1e40af', bg: '#dbeafe' },
    wip: { label: 'In Production', color: '#92400e', bg: '#fef3c7' },
    completed: { label: 'Completed', color: '#166534', bg: '#dcfce7' },
  };

  // Calculate summary
  const totalQty = workOrders.reduce((sum, wo) => sum + (wo.qty_target || 0), 0);
  const completedQty = workOrders.filter(wo => wo.status === 'completed').reduce((sum, wo) => sum + (wo.qty_target || 0), 0);
  const inProgressQty = workOrders.filter(wo => wo.status === 'wip').reduce((sum, wo) => sum + (wo.qty_target || 0), 0);
  const scheduledQty = workOrders.filter(wo => ['draft', 'released'].includes(wo.status)).reduce((sum, wo) => sum + (wo.qty_target || 0), 0);

  return (
    <div className="print-container" style={{ maxWidth: '210mm', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .report-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        .report-table th, .report-table td { border: 1px solid #e2e8f0; padding: 8px 10px; }
        .report-table th { background: #1e40af; color: white; font-weight: bold; text-align: left; }
        .report-table tr:nth-child(even) { background: #f8fafc; }
      `}</style>

      {/* Professional Header with Company Branding */}
      <div style={{ borderBottom: '3px solid #1e40af', paddingBottom: '15px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', color: '#1e40af' }}>SINGHANIA INTERNATIONAL LIMITED</h1>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#64748b' }}>
              C-25, Phase-1, Focal Point, Dhandari Kalan, Ludhiana - 141010, Punjab, India
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>
              Email: info@singhaniainternational.com | Phone: +91 161 4611418
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '9px', color: '#059669' }}>
              An ISO 9001, ISO 14001, ISO 45001 and IATF 16949 certified company
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>PRODUCTION STATUS REPORT</h2>
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#1e40af', fontWeight: 'bold' }}>{order.order_number}</p>
            <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>Generated: {formatDate(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Order & Customer Info */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <div style={{ flex: 1, background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
          <h4 style={{ margin: '0 0 8px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Customer</h4>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>{order.customer_name}</p>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b' }}>Code: {order.customer_code}</p>
          {order.customer_po_number && <p style={{ margin: '2px 0 0', fontSize: '11px' }}>PO: {order.customer_po_number}</p>}
        </div>
        <div style={{ flex: 1, background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
          <h4 style={{ margin: '0 0 8px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Order Summary</h4>
          <p style={{ margin: 0, fontSize: '12px' }}>Order Date: <strong>{formatDate(order.created_at)}</strong></p>
          <p style={{ margin: '2px 0 0', fontSize: '12px' }}>Total Items: <strong>{order.items?.length || 0}</strong></p>
          <p style={{ margin: '2px 0 0', fontSize: '12px' }}>Work Orders: <strong>{workOrders.length}</strong></p>
        </div>
      </div>

      {/* Production Progress Summary */}
      <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '6px', marginBottom: '20px', border: '1px solid #bae6fd' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 'bold', color: '#0369a1' }}>PRODUCTION PROGRESS</h4>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#166534' }}>{completedQty.toLocaleString()}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Completed</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#92400e' }}>{inProgressQty.toLocaleString()}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>In Production</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e40af' }}>{scheduledQty.toLocaleString()}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Scheduled</div>
          </div>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a' }}>{totalQty.toLocaleString()}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Total Qty</div>
          </div>
        </div>
        {totalQty > 0 && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ background: '#e2e8f0', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ 
                background: 'linear-gradient(to right, #166534 0%, #166534 ' + (completedQty/totalQty*100) + '%, #92400e ' + (completedQty/totalQty*100) + '%, #92400e ' + ((completedQty+inProgressQty)/totalQty*100) + '%, #1e40af ' + ((completedQty+inProgressQty)/totalQty*100) + '%)',
                height: '100%',
                width: ((completedQty + inProgressQty + scheduledQty) / totalQty * 100) + '%'
              }}></div>
            </div>
            <div style={{ fontSize: '10px', color: '#64748b', textAlign: 'center', marginTop: '4px' }}>
              {Math.round(completedQty/totalQty*100)}% Complete
            </div>
          </div>
        )}
      </div>

      {/* Work Orders Detail */}
      <h3 style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 'bold', color: '#1e40af', borderBottom: '1px solid #e2e8f0', paddingBottom: '5px' }}>
        WORK ORDER SCHEDULE
      </h3>
      
      {workOrders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
          No work orders have been created for this order yet.
        </div>
      ) : (
        <table className="report-table">
          <thead>
            <tr>
              <th style={{ width: '12%' }}>WO Number</th>
              <th style={{ width: '18%' }}>Part Number</th>
              <th style={{ width: '12%' }}>Machine</th>
              <th style={{ width: '10%', textAlign: 'right' }}>Quantity</th>
              <th style={{ width: '12%' }}>Plan Start</th>
              <th style={{ width: '12%' }}>Plan End</th>
              <th style={{ width: '12%' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {workOrders.map((wo) => {
              const statusInfo = STATUS_INFO[wo.status] || STATUS_INFO.draft;
              return (
                <tr key={wo.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{wo.wo_number}</td>
                  <td style={{ fontFamily: 'monospace' }}>{wo.part_internal_no}</td>
                  <td>{wo.machine_name || '-'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{wo.qty_target?.toLocaleString()}</td>
                  <td>{formatDate(wo.planned_start_date)}</td>
                  <td>{formatDate(wo.planned_end_date)}</td>
                  <td>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      background: statusInfo.bg,
                      color: statusInfo.color
                    }}>
                      {statusInfo.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Footer */}
      <div style={{ marginTop: '30px', textAlign: 'center', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
        <p style={{ margin: 0, fontSize: '10px', color: '#64748b' }}>
          For queries, please contact: info@singhaniainternational.com | +91 161 4611418
        </p>
        <p style={{ margin: '5px 0 0', fontSize: '9px', color: '#94a3b8' }}>
          www.sturdfix.com | www.singhaniainternational.com
        </p>
      </div>
    </div>
  );
}



// ============== RM REQUIREMENTS REPORT PRINT VIEW ==============

